/**
 * ImpressionContextMenu — right-click menu for the IMPRESSION section header
 * or any impression item. The single action is "Regenerate impression"
 * (re-streams the impression from the findings), mirroring the ↻ button at
 * the section header. Same portal + outside-click / Esc dismissal as the chip
 * and section menus, reusing the shared `.chip-context-menu` styling.
 *
 * Right-clicking a finding chip inside an impression item still routes to the
 * chip menu (PrototypeReport's contextmenu handler checks chips first).
 */

import { createPortal } from "react-dom";

import { useContextMenu } from "./useContextMenu";

export interface ImpressionContextMenuProps {
  anchor: { x: number; y: number };
  /** Disabled while a regeneration is already streaming. */
  disabled: boolean;
  onRegenerate: () => void;
  onClose: () => void;
}

const MENU_WIDTH = 230;

export function ImpressionContextMenu({
  anchor,
  disabled,
  onRegenerate,
  onClose,
}: ImpressionContextMenuProps) {
  const { ref, style } = useContextMenu(anchor, onClose, MENU_WIDTH);

  return createPortal(
    <div
      ref={ref}
      className="chip-context-menu"
      role="menu"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        role="menuitem"
        type="button"
        className="chip-context-menu-item"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          onRegenerate();
          onClose();
        }}
      >
        <span>{disabled ? "Regenerating…" : "Regenerate impression"}</span>
      </button>
    </div>,
    document.body,
  );
}
